/**
 * VeriDoc Global Design Tokens (Layer 1: raw physical values)
 *
 * Single source of truth for all design tokens. Tailwind config consumes
 * this file; React components can either use Tailwind classes (preferred)
 * or import these constants directly.
 *
 * Spec: docs/design/design-tokens.md
 */

// -----------------------------------------------------------------------------
// Colors
// -----------------------------------------------------------------------------

export const colors = {
  // Neutral scale (Ink series — Linear-style restrained zinc tone)
  ink: {
    0: '#FFFFFF',
    50: '#FAFAFA',
    100: '#F4F4F5',
    200: '#E4E4E7',
    300: '#D4D4D8',
    400: '#A1A1AA',
    500: '#71717A',
    600: '#52525B',
    700: '#3F3F46',
    800: '#27272A',
    900: '#18181B',
    1000: '#000000',
  },

  // Brand blue (cool, distinct from purple SaaS palette — legal/trust tone)
  brand: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
  },

  // Semantic colors (kept minimal — fg + bg pair)
  semantic: {
    success: '#16A34A',
    successBg: '#F0FDF4',
    danger: '#DC2626',
    dangerBg: '#FEF2F2',
    warning: '#CA8A04',
    warningBg: '#FEFCE8',
    info: '#0891B2',
    infoBg: '#ECFEFF',
  },

  // Decision state (review accept/reject visual encoding — M3 B5 spec)
  decision: {
    'winner-bg': '#F0FDF4',       // adopted side — light green
    'winner-fg': '#18181B',       // adopted text stays full contrast
    'winner-border': '#16A34A',   // adopted side ring + bar
    'loser-bg': '#F4F4F5',        // discarded side — muted
    'loser-fg': '#71717A',        // discarded text fades
    'loser-border': '#D4D4D8',    // discarded border subtle
    'loser-decoration': '#A1A1AA',// strikethrough line colour
    'chip-winner-bg': '#16A34A',  // winner chip solid fill
    'chip-winner-fg': '#FFFFFF',  // winner chip text
    'chip-loser-bg': '#F4F4F5',   // loser chip background
    'chip-loser-fg': '#52525B',   // loser chip text
    'chip-loser-border': '#D4D4D8',
    'selection-ring': '#3B82F6',  // j/k navigation cursor ring
  },

  // Change-highlight (business-semantic, NOT decorative — see ADR-105 §5)
  change: {
    'addition-bg': '#DCFCE7',
    'addition-fg': '#14532D',
    'addition-bar': '#16A34A',
    'deletion-bg': '#FEE2E2',
    'deletion-fg': '#7F1D1D',
    'deletion-bar': '#DC2626',
    'modification-bg': '#FEF3C7',
    'modification-fg': '#713F12',
    'modification-bar': '#CA8A04',
    'move-bg': '#DBEAFE',
    'move-fg': '#1E3A8A',
    'move-bar': '#2563EB',
    'format-bg': '#F5F0FF',
    'format-fg': '#6D28D9',
    'format-bar': '#7C3AED',
  },
} as const;

// -----------------------------------------------------------------------------
// Typography
// -----------------------------------------------------------------------------

export const typography = {
  fontFamily: {
    sans: ['"Inter"', '"PingFang SC"', '"Hiragino Sans GB"', 'system-ui', 'sans-serif'],
    serif: ['"IBM Plex Serif"', '"Source Han Serif CN"', 'Georgia', 'serif'],
    mono: ['"JetBrains Mono"', '"SF Mono"', 'Menlo', 'monospace'],
  },
  fontSize: {
    xs: '12px',
    sm: '13px',
    base: '14px', // tool-grade base (Linear-style), NOT 16px consumer
    md: '15px',
    lg: '17px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '32px',
    '4xl': '48px',
  },
  lineHeight: {
    tight: 1.3,
    normal: 1.5,
    relaxed: 1.7, // doc-reading area
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

// -----------------------------------------------------------------------------
// Spacing (4px grid)
// -----------------------------------------------------------------------------

export const spacing = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
  // Larger slots matched to Tailwind defaults so utility classes like
  // `w-24` (96px), `w-36` (144px), `w-52` (208px), `w-64` (256px), `w-80`
  // (320px) used across the library actually generate CSS rules. The
  // preset's `theme.spacing` override completely replaces Tailwind's
  // default scale, so any slot omitted here silently drops every
  // `w-N` / `h-N` / `p-N` / `gap-N` that references it.
  24: '96px',
  28: '112px',
  32: '128px',
  36: '144px',
  40: '160px',
  44: '176px',
  48: '192px',
  52: '208px',
  56: '224px',
  60: '240px',
  64: '256px',
  72: '288px',
  80: '320px',
  96: '384px',
} as const;

// -----------------------------------------------------------------------------
// Radius
// -----------------------------------------------------------------------------

export const radius = {
  none: '0',
  sm: '3px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const;

// -----------------------------------------------------------------------------
// Elevation (max 3 levels — Linear restraint)
// -----------------------------------------------------------------------------

export const elevation = {
  none: 'none',
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.05)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.05)',
} as const;

// -----------------------------------------------------------------------------
// Transition
// -----------------------------------------------------------------------------

export const transition = {
  duration: {
    fast: '150', // ms (Tailwind duration value format — bare number string)
    normal: '250',
    slow: '400',
  },
  easing: {
    default: 'ease-out',
    spring: 'cubic-bezier(0.16, 1, 0.3, 1)',
  },
} as const;
